/**
 * Per-answer anti-cheat telemetry collector.
 *
 * The blob returned by `.snapshot(event)` is attached to the outgoing
 * answer POST (see `LiveAnswerRequest.client_signals` and
 * `SubmitAnswerBody.client_signals` on the backend). It is UNTRUSTED
 * INPUT — the server persists it verbatim onto the answer row for
 * admin review only. Nothing in this file, and nothing the server does
 * with the result, should ever feed back into scoring, points, or
 * payouts. See docs above `schemas/client_signals.py` for the ground
 * rules.
 *
 * Lifecycle: one collector per question.
 *   - `startCollector({ questionRenderedAt })` — called when the
 *     question card first mounts.
 *   - `.snapshot({ answerEvent })` — called synchronously inside the
 *     answer click handler; returns the blob to POST. Also disposes
 *     the collector's DOM listeners.
 *   - `.dispose()` — called if the collector wasn't drained (timeout
 *     without a pick, or navigation).
 *
 * The collector runs entirely in page-context — it observes what the
 * page can observe, and a competent bot with page-context access can
 * of course spoof any of these signals. That's fine: this is an
 * *evidence-collection* layer, not an authenticator. A real player's
 * combination of `is_trusted: true`, non-null `pointer_type`, and
 * bounded `visibility_changes` is trivially cheap; a bot that
 * carefully forges all of them still leaves the pattern-of-forgery
 * that admin review can weigh alongside game-play signals.
 */

/**
 * Cap on the `visibility_changes` counter. Prevents a page glitching
 * between visible/hidden repeatedly from ballooning the blob — the
 * signal is "did the player app-switch at all," so 500+ is not more
 * informative than 500.
 */
const MAX_VISIBILITY_EVENTS = 500;

/**
 * Frontend build hash — a git short-SHA baked into the bundle by
 * `vite.config.js` (see the `__BUILD_VERSION__` define). Read once at
 * module load so we don't chase the global on every snapshot; if
 * Vite's define didn't fire (dev server without define, tests, SSR),
 * we resolve to `null` rather than throwing so the collector still
 * works. Used server-side to distinguish "old client that predates
 * anti-cheat" from "modern client that suspiciously stripped its
 * signals" — see the `signals_present_ratio` admin metric.
 */
const _FRONTEND_BUILD = (() => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof __BUILD_VERSION__ === 'string' && __BUILD_VERSION__.length > 0) {
      // eslint-disable-next-line no-undef
      return __BUILD_VERSION__;
    }
  } catch {
    /* not defined at runtime */
  }
  return null;
})();

/**
 * z-index threshold above which we mark a new fixed overlay as
 * suspicious. The observed userscripts spawn their status pill at
 * `999999` or `2147483647` (the max int32) so 100000 is well above
 * anything a legit modal / toast in this app uses (real modals are in
 * the 1000-9999 range per base.css) and well below the bot's noise
 * floor.
 */
const OVERLAY_ZINDEX_THRESHOLD = 100000;

// ---------------------------------------------------------------------
// Module-load canaries (PR-B).
// ---------------------------------------------------------------------
//
// Ideally we'd run these BEFORE any userscript could patch the
// prototypes we depend on. In practice a `@run-at document-start`
// userscript still wins the race — Vite loads our module after HTML
// parse. So the canary is not "the userscript hasn't tampered", but
// "the userscript's tampering is visible from our module's frame of
// reference." Combined with the answer-time `isTrusted` check, that's
// still a strong tell — a bot has to tamper before we mount AND spoof
// the click event AND avoid dropping any of the other signal patterns,
// simultaneously.
//
// Results are captured into `_NATIVE_CANARY` and surface as fields on
// every subsequent client_signals blob so admin review can see them
// alongside per-answer state.

function _looksNative(fn) {
  // A native browser function stringifies as `function name() { [native
  // code] }`. A JS reimplementation stringifies as its own source. Any
  // userscript that overrides addEventListener / attachShadow / etc.
  // trips this (unless it also overrides Function.prototype.toString,
  // which some do — hence multiple redundant canaries below).
  if (typeof fn !== 'function') return false;
  try {
    return Function.prototype.toString.call(fn).includes('[native code]');
  } catch {
    return false;
  }
}

const _NATIVE_CANARY = (() => {
  // Fires exactly once per page load, at module import time. Cached in
  // a module-scope const so per-answer snapshots don't pay for the
  // probes repeatedly.
  const out = {
    native_api_tampered: false,
    event_prototype_tampered: false,
    shadow_open_detected: false, // filled in by mountQuestionView canary
  };
  try {
    const nativeChecks = [
      typeof Element !== 'undefined' && Element.prototype && Element.prototype.attachShadow,
      typeof document !== 'undefined' && document.addEventListener,
      typeof window !== 'undefined' && window.getComputedStyle,
      typeof window !== 'undefined' && window.MutationObserver,
    ];
    if (nativeChecks.some((fn) => fn && !_looksNative(fn))) {
      out.native_api_tampered = true;
    }
  } catch {
    // If the checks themselves throw, that's already anomalous — but
    // don't let the canary crash the collector.
    out.native_api_tampered = true;
  }
  try {
    // Probe: a plain synthetic MouseEvent must report `isTrusted:
    // false`. If a userscript has redefined the getter to always
    // return true, this fires — and the bot's `.click()` on a shadow
    // button would look "trusted" downstream. We can't undo that but
    // we can flag it.
    const probe = new MouseEvent('click');
    if (probe.isTrusted !== false) out.event_prototype_tampered = true;
  } catch {
    out.event_prototype_tampered = true;
  }
  return out;
})();

/**
 * Set from `lib/question_view.js` on the first `mountQuestionView`
 * call where `host.shadowRoot !== null` after `attachShadow({ mode:
 * 'closed' })`. Sticky for the page's lifetime — once tampering is
 * observed, every subsequent client_signals blob carries the flag so
 * admins can see the full session context, not just the answer during
 * which the check fired.
 */
export function markShadowOpenDetected() {
  _NATIVE_CANARY.shadow_open_detected = true;
}

export function startCollector({ questionRenderedAt } = {}) {
  const start = Number.isFinite(questionRenderedAt) ? questionRenderedAt : performance.now();

  // Latest pointerdown context. Updated on every real pointerdown that
  // reaches the document during the question. Reset per collector so a
  // pointer left over from the previous question doesn't leak in.
  let latestPointer = {
    pointer_type: null,
    pointer_radius_x: null,
    pointer_radius_y: null,
    pointer_force: null,
  };

  let visibilityChanges = 0;
  let overlayDetected = false;
  // Decoy trap (PR 4): flipped when a decoy element inside the light
  // DOM sibling of the shadow host receives a click. A human cannot see
  // (color: transparent) or tap (pointer-events: none, tabindex: -1,
  // aria-hidden: true) the decoy, so this is near-binary evidence of a
  // screen-scraping bot's `.click()` sequence.
  let decoyHit = false;
  // PR-B: flipped when we observed a pointerdown whose `isTrusted` was
  // false. A bot that pre-arms `pointer_type: 'touch'` on a synthetic
  // PointerEvent to look human-like still can't forge `isTrusted` (it's
  // a native getter), so this catches that specific spoof pattern.
  let pointerTypeUntrusted = false;
  let disposed = false;

  const onPointerDown = (ev) => {
    if (!ev || typeof ev !== 'object') return;
    // PR-B: only mutate `latestPointer` on TRUSTED pointerdowns. A bot
    // that dispatches a synthetic PointerEvent to pre-arm plausible
    // touch geometry now leaves `latestPointer` untouched (so the
    // outgoing blob shows `pointer_type: null` — a mild tell on its
    // own) AND we record the attempt via `pointerTypeUntrusted` for
    // admin review. Real touches / accessibility taps flow through
    // unchanged.
    if (ev.isTrusted !== true) {
      pointerTypeUntrusted = true;
      return;
    }
    const pt = typeof ev.pointerType === 'string' ? ev.pointerType : null;
    // width/height on PointerEvent map to touch major/minor radius on
    // real fingers (values in CSS pixels). Non-touch pointers report
    // 1 which is fine — we just capture whatever's there.
    const width = Number.isFinite(ev.width) ? ev.width : null;
    const height = Number.isFinite(ev.height) ? ev.height : null;
    const pressure = Number.isFinite(ev.pressure) ? ev.pressure : null;
    latestPointer = {
      pointer_type: pt || null,
      pointer_radius_x: width != null ? Math.max(0, width) : null,
      pointer_radius_y: height != null ? Math.max(0, height) : null,
      pointer_force: pressure != null ? Math.max(0, pressure) : null,
    };
  };

  const onVisibilityLike = () => {
    if (visibilityChanges < MAX_VISIBILITY_EVENTS) visibilityChanges += 1;
  };

  // Watch document.body for a new top-level fixed overlay. This mirrors
  // the observed userscripts' status-pill injection (they use extreme
  // z-index values). We only inspect DIRECT children of body to keep
  // this cheap on question-start — the userscripts inject at that level.
  let observer = null;
  const startOverlayObserver = () => {
    if (typeof MutationObserver !== 'function' || !document.body) return null;
    const mo = new MutationObserver((mutations) => {
      if (overlayDetected) return;
      for (const m of mutations) {
        if (m.type !== 'childList') continue;
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const cs = window.getComputedStyle(node);
          if (cs.position !== 'fixed') continue;
          const z = parseInt(cs.zIndex, 10);
          if (!Number.isFinite(z) || z < OVERLAY_ZINDEX_THRESHOLD) continue;
          overlayDetected = true;
          return;
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: false });
    return mo;
  };

  // Attach listeners. `capture: true` on pointerdown so we see the
  // event before any handler that might stopPropagation — otherwise a
  // click inside a shadow root would swallow the pointerdown context
  // we need. `passive: true` because we never preventDefault here.
  document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  document.addEventListener('visibilitychange', onVisibilityLike, { passive: true });
  window.addEventListener('blur', onVisibilityLike, { passive: true });
  observer = startOverlayObserver();

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    document.removeEventListener('pointerdown', onPointerDown, { capture: true });
    document.removeEventListener('visibilitychange', onVisibilityLike);
    window.removeEventListener('blur', onVisibilityLike);
    if (observer) observer.disconnect();
    observer = null;
  };

  return {
    /**
     * Produce the outgoing signals blob for this answer. `answerEvent`
     * is the browser Event object that fired the answer click; passing
     * a truthy `.isTrusted` flag is the strongest single tell.
     *
     * Disposes the collector as a side-effect. Safe to call at most
     * once — subsequent calls return the same shape but with no
     * further state updates.
     */
    snapshot({ answerEvent } = {}) {
      const isTrusted = answerEvent && typeof answerEvent === 'object'
        ? !!answerEvent.isTrusted
        : null;
      // Round to whole ms so the JSON column doesn't carry noisy
      // sub-millisecond floats. `performance.now()` returns a float.
      const msSinceRender = Math.max(0, Math.round(performance.now() - start));
      const out = {
        is_trusted: isTrusted,
        pointer_type: latestPointer.pointer_type,
        pointer_radius_x: latestPointer.pointer_radius_x,
        pointer_radius_y: latestPointer.pointer_radius_y,
        pointer_force: latestPointer.pointer_force,
        ms_since_render: msSinceRender,
        visibility_changes: visibilityChanges,
        overlay_detected: overlayDetected,
        // PR-B canaries. Only emit the truthy fields — `to_storage_dict`
        // on the backend drops undefined, so legit-player blobs stay
        // compact and admin drill-downs only surface the anomalies.
        decoy_hit: decoyHit ? true : undefined,
        pointer_type_untrusted: pointerTypeUntrusted ? true : undefined,
        // Module-load canaries (page-lifetime scoped, not per-answer).
        // Sticky once observed so downstream admin queries can group
        // by session even if the specific answer during which the
        // check fired isn't the one being reviewed.
        native_api_tampered: _NATIVE_CANARY.native_api_tampered ? true : undefined,
        event_prototype_tampered: _NATIVE_CANARY.event_prototype_tampered
          ? true
          : undefined,
        shadow_open_detected: _NATIVE_CANARY.shadow_open_detected ? true : undefined,
        // PR-E: frontend build hash. Always emitted (page-lifetime
        // constant). `to_storage_dict` on the backend drops null so
        // dev-mode / non-baked builds don't add noise.
        frontend_build: _FRONTEND_BUILD || undefined,
      };
      dispose();
      return out;
    },
    /**
     * Flag that a decoy element in the light DOM was clicked. Called
     * from the shared question view's decoy click handler (see
     * `mountDecoyBlock` in lib/question_view.js). Idempotent — repeat
     * calls just keep the flag true.
     */
    markDecoyHit() {
      decoyHit = true;
    },
    /** Idempotent teardown for the timeout / navigation paths. */
    dispose,
  };
}
